import { useState, useEffect, useRef } from 'react';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { useAppSelector } from '../hooks/useAppSelector';
import {
  updateEmployee,
  selectEmployeesStatus,
  setEditingQualification,
  updateEditingQualification,
  clearEditingQualification,
  selectEditingQualification,
} from '../../slices/employees/employeesSlice';
import { Employee, EmployeeQualification } from '../../models/employee.model';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

export function useQualificationsCardForm(employee: Partial<Employee>) {
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const [showAddSocialModal, setShowAddSocialModal] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);

  // Get editing state from Redux
  const editingQualification = useAppSelector(selectEditingQualification);
  const status = useAppSelector(selectEmployeesStatus);

  // Check if this qualification is being edited
  const currentQualification = employee.qualifications?.[0];
  const isEdited = !!editingQualification && editingQualification.employee_id === employee.id;

  // Initialize the editing qualification in Redux if needed
  useEffect(() => {
    if (employee && currentQualification && !editingQualification && isEdited) {
      dispatch(
        setEditingQualification({
          ...currentQualification,
          employee_id: employee.id,
        })
      );
    }
  }, [employee, currentQualification, editingQualification, dispatch, isEdited]);

  const handleChange =
    <K extends keyof EmployeeQualification>(field: K) =>
    (value: any) => {
      if (isEdited) {
        dispatch(updateEditingQualification({ field, value }));
      }
    };

  const cancelChanges = () => {
    dispatch(clearEditingQualification());
  };

  const saveChanges = async () => {
    if (isEdited && editingQualification && employee.id) {
      dispatch(showOverlay());
      await dispatch(
        updateEmployee({
          employeeId: employee.id,
          qualificationData: editingQualification,
          qualificationId: currentQualification?.id,
        })
      );
      dispatch(hideOverlay());
      setShowModal(false);
    }
  };

  const toggleEditing = () => {
    if (isEdited) {
      dispatch(clearEditingQualification());
    } else if (currentQualification) {
      dispatch(
        setEditingQualification({
          ...currentQualification,
          employee_id: employee.id,
        })
      );
    } else {
      dispatch(
        setEditingQualification({
          degree: '',
          university_name: '',
          start_date: '',
          end_date: '',
          employee_id: employee.id,
        } as Partial<EmployeeQualification>)
      );
    }
  };

  const toogleEditingSocialModal = () => {
    setShowAddSocialModal((prev) => !prev);
  };

  const confirmExit = () => {
    dispatch(clearEditingQualification());
    setShowModal(false);
  };

  // Handle clicks outside the form
  useEffect(() => {
    let showModalTimeout: ReturnType<typeof setTimeout> | undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (isEdited && formRef.current && !formRef.current.contains(event.target as Node)) {
        const targetElement = event.target as HTMLElement;
        if (
          targetElement.closest('.MuiDatePicker-root') ||
          targetElement.closest('.MuiPickerPopper-root') ||
          targetElement.closest('.MuiLocalizationProvider-root') ||
          targetElement.closest('.MuiDialog-root')
        ) {
          return;
        }

        showModalTimeout = setTimeout(() => {
          setShowModal(true);
        }, 0);
      }
    };
    if (isEdited) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (showModalTimeout) clearTimeout(showModalTimeout);
    };
  }, [isEdited]);

  return {
    formData: editingQualification || currentQualification || {},
    handleChange,
    saveChanges,
    isEdited,
    toggleEditing,
    formRef,
    showModal,
    setShowModal,
    confirmExit,
    showAddSocialModal,
    setShowAddSocialModal,
    toogleEditingSocialModal,
    isLoading: status === 'loading',
    cancelChanges,
  };
}
